import React, { useState, useCallback } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Link as MuiLink,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption } from "@/components/Button";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AddressListGrid } from "@/features/dmManagement/components/AddressListGrid";
import { PostAddressListConfirmAPI } from "@/features/dmManagement/api/postAddressListConfirmAPI";
import { GetAddressListDataAPI } from "@/features/dmManagement/api/getAddressListDataAPI";
import { GetReportAPI } from "@/features/dmManagement/api/getReportAPI";
import { GetAddressListDownloadAPI } from "@/features/dmManagement/api/getAddressListDownloadAPI";
import { useApiClient } from "@/hooks/useApiClient";
import type { AddressListStatus, Order } from "@/features/dmManagement/types";
import type { KeyedMutator } from "swr";

interface AddressListDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  addressListStatus: AddressListStatus;
  hasReport: boolean;
  orderMutate: KeyedMutator<Order>;
  onReupload?: () => void;
}

/** 承認ボタン表示: 確認待ちステータスのときのみ */
const showConfirmButton = (status: AddressListStatus): boolean =>
  status === "require_confirmation";

/** 再アップロードボタン表示: 確認待ちまたは再アップロード待ちのとき */
const showReuploadButton = (status: AddressListStatus): boolean =>
  status === "require_confirmation" || status === "require_reupload";

/**
 * 宛先リスト確認ダイアログ
 * 宛先リストのプレビュー表示・承認・再アップロード・レポートダウンロードを行うモーダル
 */
export const AddressListDialog: React.FC<AddressListDialogProps> = ({
  open,
  onClose,
  orderId,
  addressListStatus,
  hasReport,
  orderMutate,
  onReupload,
}) => {
  const { apiClient } = useApiClient();
  const { data: addressListData, isLoading: isLoadingData } =
    GetAddressListDataAPI(apiClient, orderId, open);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = useCallback(async (): Promise<void> => {
    setIsConfirming(true);
    try {
      await PostAddressListConfirmAPI(apiClient, orderId);
      await orderMutate();
      toast.success("宛先リストを承認しました");
      onClose();
    } catch {
      toast.error("承認に失敗しました");
    } finally {
      setIsConfirming(false);
    }
  }, [apiClient, orderId, orderMutate, onClose]);

  const handleReportDownload = useCallback(async (): Promise<void> => {
    try {
      await GetReportAPI(apiClient, orderId);
    } catch {
      toast.error("レポートのダウンロードに失敗しました");
    }
  }, [apiClient, orderId]);

  const handleAddressListDownload = useCallback(async (): Promise<void> => {
    try {
      await GetAddressListDownloadAPI(apiClient, orderId);
    } catch {
      toast.error("宛先リストのダウンロードに失敗しました");
    }
  }, [apiClient, orderId]);

  const handleModalClose = (
    _event: Record<string, unknown>,
    _reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    onClose();
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleModalClose}
      ariaLabelledby="address-list-dialog"
      ariaDescribedby="address-list-dialog-description"
      width={"1200px"}
      maxHeight={"90vh"}
      overflow="auto"
    >
      <Stack direction={"column"} spacing={3}>
        <PageTitle>宛先リスト</PageTitle>
        <Box>
          {isLoadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {addressListStatus === "require_confirmation" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  レポートの内容を確認し、問題なければ承認をしてください。内容に問題がある場合は修正した宛先リストをアップロードをしてください。
                </Alert>
              )}
              {addressListStatus === "require_reupload" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  レポートの内容を確認し修正した宛先リストをアップロードしてください。
                </Alert>
              )}
              <AddressListGrid
                rows={addressListData?.rows ?? []}
                totalRows={addressListData?.totalRows ?? 0}
                hasError={addressListData?.hasError}
                hasWarning={addressListData?.hasWarning}
              />
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <MuiLink
                  component="button"
                  onClick={() => {
                    void handleAddressListDownload();
                  }}
                >
                  宛先リスト（原本）をダウンロード
                </MuiLink>
                {hasReport && (
                  <MuiLink
                    component="button"
                    onClick={() => {
                      void handleReportDownload();
                    }}
                  >
                    レポートをダウンロード
                  </MuiLink>
                )}
              </Box>
            </>
          )}
        </Box>
        <Stack
          direction={"row"}
          spacing={2}
          marginTop={2}
          justifyContent={"center"}
        >
          <Button
            label="閉じる"
            onClick={onClose}
            disabled={isConfirming}
            size={SizingWrapperStyle.INHERIT}
            variant={ButtonVariantOption.Outlined}
          />
          {showReuploadButton(addressListStatus) && onReupload && (
            <Button
              label="再アップロード"
              onClick={onReupload}
              disabled={isConfirming}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Outlined}
            />
          )}
          {showConfirmButton(addressListStatus) && (
            <Button
              label="承認する"
              onClick={() => {
                void handleConfirm();
              }}
              disabled={isConfirming}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
              hasLoading={true}
              loading={isConfirming}
            />
          )}
        </Stack>
      </Stack>
    </CustomModal>
  );
};
